import React from "react";
import { X, Car, MapPin, Calendar, Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useDriver } from "@/hooks/useDrivers";
import { TrackTypeBadge } from "@/components/tracks/TrackTypeBadge";
import { formatUserName } from "@/utils/userName";

interface DriverModalProps {
  isOpen: boolean;
  onClose: () => void;
  driverId: string;
}

export default function DriverModal({
  isOpen,
  onClose,
  driverId,
}: DriverModalProps) {
  const { data: driver, isLoading, error } = useDriver(driverId);
  const { t } = useTranslation(["pages", "common"]);

  if (!isOpen) return null;

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div
 className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-2 sm:p-4 z-50"
      onClick={handleBackdropClick}
    >
 <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[95vh] sm:max-h-[90vh] overflow-y-auto">
 <div className="flex items-center justify-between p-4 sm:p-6 border-b border-secondary-200">
          <h2 className="text-lg sm:text-xl font-semibold text-secondary-900">
            {t("pages:drivers.modal.driverInformation")}
          </h2>
          <button
            onClick={onClose}
 className="text-secondary-400 hover:text-secondary-600 transition-colors p-1"
          >
 <X className="h-5 w-5 sm:h-6 sm:w-6" />
          </button>
        </div>

 <div className="p-4 sm:p-6">
          {isLoading && (
 <div className="flex flex-col sm:flex-row items-center justify-center py-6 sm:py-8">
 <Loader2 className="h-6 w-6 sm:h-8 sm:w-8 animate-spin text-primary-600" />
              <span className="mt-2 sm:mt-0 sm:ml-2 text-secondary-600 text-sm sm:text-base">
                {t("pages:drivers.modal.loading")}
              </span>
            </div>
          )}

          {error && (
            <div className="text-red-600 text-center py-8">
              {t("pages:drivers.modal.failedToLoad")}
            </div>
          )}

          {driver && (
 <div className="space-y-6">
              {/* Driver Info */}
              <div>
 <h3 className="text-lg font-semibold text-secondary-900 mb-4">
                  {formatUserName(driver)}
                </h3>
 <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 text-sm">
                  <div className="flex items-center text-secondary-600">
                    <Car className="h-4 w-4 mr-2" />
                    <span>{t("pages:drivers.modal.vehiclesOwned", { count: driver.vehicleCount })}</span>
                  </div>
                  <div className="flex items-center text-secondary-600">
                    <Calendar className="h-4 w-4 mr-2" />
                    <span>{t("pages:drivers.modal.totalSessions", { count: driver.sessionCount })}</span>
                  </div>
                </div>
              </div>

              {/* Vehicles */}
              <div>
                <h4 className="text-md font-semibold text-secondary-900 mb-3">
                  {t("pages:drivers.modal.vehiclesCount", { count: driver.vehicles.length })}
                </h4>
                {driver.vehicles.length > 0 ? (
 <div className="space-y-2">
                    {driver.vehicles.map((vehicle) => (
                      <div
                        key={vehicle.id}
 className="p-3 bg-secondary-50 rounded-lg"
                      >
 <div className="flex items-start sm:items-center">
 <Car className="h-4 w-4 text-secondary-500 mr-3 mt-1 sm:mt-0 flex-shrink-0" />
 <div className="flex-1 min-w-0">
 <div className="font-medium text-secondary-900 text-sm sm:text-base break-words">
                              {vehicle.name ||
                                `${vehicle.year} ${vehicle.maker} ${vehicle.model}`}
                            </div>
                            {vehicle.name && (
 <div className="text-xs sm:text-sm text-secondary-600 break-words">
                                {vehicle.year} {vehicle.maker}{" "}
                                {vehicle.model}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-secondary-500 text-sm">
                    {t("pages:drivers.modal.noVehiclesRegistered")}
                  </p>
                )}
              </div>

              {/* Most Visited Tracks */}
              <div>
                <h4 className="text-md font-semibold text-secondary-900 mb-3">
                  {t("pages:drivers.modal.mostVisitedTracks")}
                </h4>
                {driver.topTracks.length > 0 ? (
 <div className="space-y-2">
                    {driver.topTracks.map((track) => (
                      <div
                        key={track.id}
 className="flex items-center justify-between p-3 bg-secondary-50 rounded-lg"
                      >
 <div className="flex items-center">
 <MapPin className="h-4 w-4 text-secondary-500 mr-3" />
                          <div>
                            <div className="font-medium text-secondary-900">
                              {track.name}
                            </div>
                            <div className="mt-1">
                              <TrackTypeBadge type={track.type} />
                            </div>
                          </div>
                        </div>
                        <div className="text-sm text-secondary-600">
                          {track.sessionCount}{" "}
                          {t("pages:drivers.modal.session", { count: track.sessionCount })}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-secondary-500 text-sm">{t("pages:drivers.modal.noTrackActivityYet")}</p>
                )}
              </div>

              {/* Member Since */}
 <div className="pt-4 border-t border-secondary-200">
              <span className="text-sm text-secondary-500">
                {t("pages:drivers.modal.memberSince", { date: new Date(driver.createdAt).toLocaleDateString() })}
              </span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
